<template>
    <div class="guest-view-container">
        <!-- Modern Header Section -->
        <div class="header-section">
            <div class="header-content">
                <div class="title-section">
                    <div class="page-title">
                        <div class="title-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                            </svg>
                        </div>
                        <div class="title-content">
                            <h1>New Bookings</h1>
                            <p class="subtitle">View and manage pending guest bookings</p>
                        </div>
                    </div>
                </div>
                <div class="header-actions">
                    <button class="btn-secondary" @click="goBack" aria-label="Go back">
                        <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        Back
                    </button>
                </div>
            </div>
        </div>

        <!-- Stats Cards -->
        <div class="stats-section">
            <div class="stat-card">
                <div class="stat-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                </div>
                <div class="stat-content">
                    <h3>{{ totalCount }}</h3>
                    <p>Pending Bookings</p>
                </div>
            </div>
        </div>

        <!-- Main Content Area -->
        <div class="main-content">

            <!-- Enhanced Table Container -->
            <div class="table-container">
                <!-- Loading Overlay for Table -->
                <div class="table-loading-overlay" v-if="isFetching">
                    <div class="loading-content">
                        <div class="spinner"></div>
                        <p class="loading-text">Loading bookings...</p>
                    </div>
                </div>
                
                <div class="table-header">
                    <h2>Pending Bookings List</h2>
                    <div class="table-controls">
                        <div class="search-box">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="11" cy="11" r="8"/>
                                <path d="m21 21-4.35-4.35"/>
                            </svg>
                            <input 
                                type="text" 
                                placeholder="Search bookings..." 
                                v-model="searchQuery"
                                @input="handleSearch"
                            />
                        </div>
                    </div>
                </div>
                
                <div class="table-wrapper">
                    <table class="modern-table bookings-table">
                        <thead v-if="guests.length > 0 && !isFetching">
                            <tr>
                                <th class="col-index">#</th>
                                <th class="col-guest-id">Guest ID</th>
                                <th class="col-mobile">Mobile Number</th>
                                <th class="col-booking-id">Booking ID</th>
                                <th class="col-created">Created Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(guest, index) in guests" :key="guest.guestId" 
                                class="table-row booking-row" 
                                :class="{ 'new-booking-row': isNewBooking(guest) }">
                                <td class="col-index">{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                                <td class="col-guest-id">
                                    <div class="guest-id-wrapper">
                                        <span class="guest-id-badge">{{ guest.guestId }}</span>
                                    </div>
                                </td>
                                <td class="col-mobile">
                                    <div class="mobile-display">
                                        <svg class="mobile-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                                        </svg>
                                        <span class="mobile-number">{{ guest.mobileno || '--' }}</span>
                                    </div>
                                </td>
                                <td class="col-booking-id">
                                    <div class="booking-id-wrapper">
                                        <span class="booking-id-badge">{{ guest.purchaseId || '--' }}</span>
                                        <span v-if="isNewBooking(guest)" class="new-booking-indicator">NEW</span>
                                    </div>
                                </td>
                                <td class="col-created">
                                    <div class="date-display">
                                        <svg class="date-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                            <line x1="16" y1="2" x2="16" y2="6"/>
                                            <line x1="8" y1="2" x2="8" y2="6"/>
                                            <line x1="3" y1="10" x2="21" y2="10"/>
                                        </svg>
                                        <span class="date-text">{{ formatDate(guest.createdat) }}</span>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="guests.length === 0 && !isFetching" class="empty-row">
                                <td colspan="5" class="empty-state">
                                    <div class="empty-content">
                                        <svg class="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                            <line x1="16" y1="2" x2="16" y2="6"/>
                                            <line x1="8" y1="2" x2="8" y2="6"/>
                                            <line x1="3" y1="10" x2="21" y2="10"/>
                                        </svg>
                                        <h3>No pending bookings</h3>
                                        <p>New bookings will appear here</p>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Enhanced Pagination -->
                <div class="pagination-wrapper" v-if="totalCount > pageSize">
                    <div class="pagination-info">
                        <span>Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} entries</span>
                    </div>
                    <div class="pagination">
                        <button 
                            :disabled="currentPage === 1 || isFetching" 
                            @click="changePage(currentPage - 1)"
                            class="pagination-btn prev-btn"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M15 18l-6-6 6-6"/>
                            </svg>
                        </button>
                        
                        <button 
                            v-for="page in visiblePages" 
                            :key="page"
                            :class="['pagination-btn', { 'active': page === currentPage, 'ellipsis': page === '...' }]" 
                            :disabled="isFetching || page === '...'"
                            @click="changePage(page)"
                        >
                            {{ page }}
                        </button>
                        
                        <button 
                            :disabled="currentPage === totalPages || isFetching" 
                            @click="changePage(currentPage + 1)"
                            class="pagination-btn next-btn"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M9 18l6-6-6-6"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Toast Notifications -->
    <div class="toast-container">
        <transition-group name="toast" tag="div">
            <div 
                v-for="toast in toasts" 
                :key="toast.id" 
                class="toast"
                :class="[`toast-${toast.type}`]"
            >
                <div class="toast-icon">
                    <svg v-if="toast.type === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    <svg v-else-if="toast.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22,4 12,14.01 9,11.01"/>
                    </svg>
                    <svg v-else-if="toast.type === 'info'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="16" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                    <svg v-else-if="toast.type === 'warning'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                </div>
                <div class="toast-content">
                    <p class="toast-message">{{ toast.message }}</p>
                </div>
                <button class="toast-close" @click="removeToast(toast.id)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>
        </transition-group>
    </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';
import { readSessionToken } from '@/utilities/sessionTokenStorage';
import notificationService from '@/services/notificationService';
export default {
    name: 'NewBookingsView',
    components: {
    },
    props: {
        objectSpaces: {
            type: Array,
            required: true,
        },
        objectData: {
            type: Object,
            required: true,
        },
        isLoading: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            objectName: '',
            adminProfileData: {},
            guests: [],
            currentPage: 1,
            pageSize: 10,
            totalCount: 0,
            totalPages: 0,
            isFetching: false,
            objectId: '',
            searchQuery: '',
            searchTimeout: null,
            cacheDuration: 5 * 60 * 1000, // Cache duration: 5 minutes in milliseconds
            toasts: [],
            newBookingIds: [],
            initialNewBookingIds: [], // Store initial newBookingIds on mount (persists until refresh)
            hasMarkedAsRead: false, // Flag to prevent duplicate API calls
            notificationUpdateKey: 0
        };
    },
    computed: {
        visiblePages() {
            const range = 2;
            let pages = [];
            let start = Math.max(1, this.currentPage - range);
            let end = Math.min(this.totalPages, this.currentPage + range);

            // Always show first page
            if (start > 1) {
                pages.push(1);
                if (start > 2) pages.push('...');
            }

            // Add range around current page
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            // Always show last page
            if (end < this.totalPages) {
                if (end < this.totalPages - 1) pages.push('...');
                pages.push(this.totalPages);
            }

            return pages;
        },
        activeGuestsCount() {
            return this.totalCount;
        },
    },
    mounted() {
        this.objectName = this.$route.params.objectName;
        this.objectId = this.$route.params.id;
        let adminProfileData = localStorage.getItem('ADMIN_PROFILE_DATA');
        this.adminProfileData = JSON.parse(adminProfileData);
        // Force fresh API call on mount (page refresh)
        window.addEventListener('beforeunload', this.clearCache);
        this.fetchBookingNotifications();
        this.fetchGuests();
        
        // Check for snapshot in sessionStorage (stored before Read API was called)
        // This ensures NEW tags remain visible even if Read API was called before navigation
        const snapshotKey = `NEW_BOOKING_IDS_${this.objectId}`;
        const snapshot = sessionStorage.getItem(snapshotKey);
        if (snapshot) {
            try {
                this.initialNewBookingIds = JSON.parse(snapshot);
                this.newBookingIds = [...this.initialNewBookingIds];
            } catch (e) {
                console.error('Error parsing newBookingIds snapshot:', e);
                // Fallback to service data
                this.initialNewBookingIds = [...this.newBookingIds];
            }
        } else {
            // No snapshot - use service data (normal flow)
            this.initialNewBookingIds = [...this.newBookingIds];
        }
        
        // Subscribe to notification updates (but don't update newBookingIds after initial load)
        this.unsubscribeNotifications = notificationService.subscribe(() => {
            this.notificationUpdateKey++;
            // Don't update newBookingIds - keep initial ones until page refresh
        });
    },
    beforeUnmount() {
        // Clean up the event listener
        window.removeEventListener('beforeunload', this.clearCache);
        // Clean up snapshot from sessionStorage
        if (this.objectId) {
            const snapshotKey = `NEW_BOOKING_IDS_${this.objectId}`;
            sessionStorage.removeItem(snapshotKey);
        }
        // Unsubscribe from notifications
        if (this.unsubscribeNotifications) {
            this.unsubscribeNotifications();
        }
    },
    watch: {
        '$route.params.id'(newId) {
            if (newId && newId !== this.objectId) {
                this.objectId = newId;
                this.hasMarkedAsRead = false; // Reset flag when object changes
                this.initialNewBookingIds = []; // Reset initial IDs
                this.fetchBookingNotifications();
                this.fetchGuests(true);
                
                // Check for snapshot in sessionStorage for new object
                const snapshotKey = `NEW_BOOKING_IDS_${newId}`;
                const snapshot = sessionStorage.getItem(snapshotKey);
                if (snapshot) {
                    try {
                        this.initialNewBookingIds = JSON.parse(snapshot);
                        this.newBookingIds = [...this.initialNewBookingIds];
                    } catch (e) {
                        console.error('Error parsing newBookingIds snapshot:', e);
                        this.initialNewBookingIds = [...this.newBookingIds];
                    }
                } else {
                    this.initialNewBookingIds = [...this.newBookingIds];
                }
                
                // Mark as read for new object
                this.markNotificationsAsRead();
            }
        }
    },
    methods: {
        // Toast notification methods
        showToast(message, type = 'error', duration = 5000) {
            const id = Date.now() + Math.random(); // Ensure unique ID
            this.toasts.push({ id, message, type });
            
            if (duration > 0) {
                setTimeout(() => {
                    this.removeToast(id);
                }, duration);
            }
        },
        removeToast(id) {
            this.toasts = this.toasts.filter(toast => toast.id !== id);
        },

        // Enhanced API error handling
        handleApiError(error, context = 'Operation') {
            // Skip error handling for "no data" cases (status 400 with errorcode 2003)
            if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
                return;
            }

            console.error(`${context} error:`, error);
            
            let message = null;
            let type = 'error';

            if (error?.response?.data) {
                const errorData = error.response.data;
                const status = error.response.status;
                
                // For HTTP 400 errors, always use the reason field from API response
                if (status === 400) {
                    if (errorData.reason) {
                        message = errorData.reason;
                    }
                    // If no reason provided for 400 error, don't show toast (no generic messages)
                } else {
                    // For other status codes, prioritize reason field
                    if (errorData.reason) {
                        message = errorData.reason;
                    } else if (errorData.message) {
                        message = errorData.message;
                    } else {
                        // Fallback messages for other status codes only if no reason/message
                        switch (status) {
                            case 401:
                                message = 'Session expired. Please login again.';
                                setTimeout(() => {
                                    this.$router.push('/login');
                                }, 2000);
                                break;
                            case 403:
                                message = 'You do not have permission to perform this action.';
                                break;
                            case 404:
                                message = 'The requested resource was not found.';
                                break;
                            case 429:
                                message = 'Too many requests. Please wait a moment and try again.';
                                break;
                            case 500:
                                message = 'Server error occurred. Please try again later.';
                                break;
                            case 503:
                                message = 'Service temporarily unavailable. Please try again later.';
                                break;
                        }
                    }
                }
            } else if (error?.request) {
                message = 'No response from server. Please check your internet connection.';
            } else if (error?.message) {
                message = error.message;
            }

            // Only show toast if we have a message (genuine errors only)
            if (message) {
                this.showToast(message, type);
            }
        },

        // Helper method to get authentication token
        getAuthToken() {
            try {
                const token = readSessionToken();
                if (!token) {
                    throw new Error('No authentication token found');
                }
                return token;
            } catch (error) {
                console.error('Error getting auth token:', error);
                this.showToast('Authentication error. Please login again.', 'error');
                setTimeout(() => {
                    this.$router.push('/login');
                }, 2000);
                throw error;
            }
        },

        goBack() {
            this.$router.go(-1);
        },
        clearAllGuestCache() {
            // Clear all cached booking data for this object
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(`GUEST-`) && key.includes(`-${this.objectId}`) && key.includes(`-PENDING-ONLINE`)) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
        },
        clearCache() {
            // Clear cache for the current page
            const cacheKey = `GUEST-${this.currentPage}-${this.pageSize}-${this.objectId}-PENDING-ONLINE`;
            localStorage.removeItem(cacheKey);
        },
        async fetchGuests(forceFresh = false) {
            const cacheKey = `GUEST-${this.currentPage}-${this.pageSize}-${this.objectId}-PENDING-ONLINE`;
            const cachedData = localStorage.getItem(cacheKey);

            // Skip cache if forceFresh is true (e.g., on page refresh)
            if (!forceFresh && cachedData) {
                try {
                    const { data, timestamp } = JSON.parse(cachedData);
                    const now = new Date().getTime();
                    if (now - timestamp < this.cacheDuration) {
                        // Use cached data
                        this.guests = data.data || [];
                        this.totalCount = data.totalCount || 0;
                        this.totalPages = Math.ceil(this.totalCount / this.pageSize);
                        this.isFetching = false;
                        return;
                    }
                } catch (cacheError) {
                    console.warn('Error parsing cached data:', cacheError);
                    localStorage.removeItem(cacheKey);
                }
            }

            // Fetch from API
            try {
                this.isFetching = true;
                
                const response = await apiService.getGuests({
                    objectId: this.objectId,
                    page: this.currentPage,
                    pagesize: this.pageSize,
                    status: 'PENDING',
                    mode: 'ONLINE'
                });

                // Validate response data
                if (!response.data) {
                    throw new Error('Invalid response from server');
                }

                // Store response in cache with timestamp
                try {
                    const cacheData = {
                        data: response.data,
                        timestamp: new Date().getTime(),
                    };
                    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
                } catch (cacheError) {
                    console.warn('Error caching data:', cacheError);
                }

                // Update component data
                this.guests = response.data.data || [];
                this.totalCount = response.data.totalCount || 0;
                this.totalPages = Math.ceil(this.totalCount / this.pageSize);

                // Show success message for fresh data load (not for cached data)
                if (forceFresh && this.guests.length > 0) {
                    this.showToast(`Loaded ${this.guests.length} bookings successfully`, 'success', 3000);
                }
            } catch (error) {
                // Handle "no data" case - set empty arrays without showing error
                if (isNoDataError(error)) {
                    this.guests = [];
                    this.totalCount = 0;
                    this.totalPages = 0;
                    return;
                }
                if (isHmsSubscriptionInactiveError(error)) {
                    return;
                }
                
                this.handleApiError(error, 'Loading bookings');
                
                // Try to fall back to cached data if available
                if (cachedData) {
                    try {
                        const { data } = JSON.parse(cachedData);
                        this.guests = data.data || [];
                        this.totalCount = data.totalCount || 0;
                        this.totalPages = Math.ceil(this.totalCount / this.pageSize);
                        this.showToast('Showing cached data due to connection issues', 'warning', 4000);
                    } catch (fallbackError) {
                        console.error('Error using cached fallback:', fallbackError);
                        this.guests = [];
                        this.totalCount = 0;
                        this.totalPages = 0;
                    }
                } else {
                    this.guests = [];
                    this.totalCount = 0;
                    this.totalPages = 0;
                }
            } finally {
                this.isFetching = false;
            }
        },
        changePage(page) {
            if (
                typeof page === 'number' && 
                page >= 1 && 
                page <= this.totalPages && 
                !this.isFetching &&
                page !== this.currentPage
            ) {
                this.currentPage = page;
                this.fetchGuests(); // Use cache for pagination
            }
        },
        formatDate(dateString) {
            if (!dateString) return '--';
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
        
        formatDateShort(dateString) {
            if (!dateString) return '--';
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        },
        getInitials(name) {
            if (!name) return 'G';
            return name.split(' ')
                .map(word => word.charAt(0).toUpperCase())
                .slice(0, 2)
                .join('');
        },
        getStatusClass(status) {
            if (!status) return 'status-default';
            const statusLower = status.toLowerCase();
            switch (statusLower) {
                case 'active':
                    return 'status-active';
                case 'inactive':
                    return 'status-inactive';
                case 'pending':
                    return 'status-pending';
                case 'checked-out':
                    return 'status-checked-out';
                default:
                    return 'status-default';
            }
        },
        
        getFormStatusClass(formStatus) {
            if (!formStatus) return 'status-default';
            const statusLower = formStatus.toLowerCase();
            switch (statusLower) {
                case 'completed':
                    return 'status-active';
                case 'pending':
                    return 'status-pending';
                default:
                    return 'status-default';
            }
        },
        handleSearch() {
            // Clear existing timeout
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }
            
            // Set new timeout for debounced search
            this.searchTimeout = setTimeout(() => {
                // Reset to first page when searching
                this.currentPage = 1;
                this.fetchGuests(true); // Force fresh fetch for search
            }, 300);
        },
        async fetchBookingNotifications() {
            // Get from centralized notification service
            await notificationService.getNotifications();
            // Get new booking IDs for current object (only on initial load)
            // After initial load, use initialNewBookingIds to persist NEW tags
            const currentIds = notificationService.getNewBookingIds(this.objectId);
            if (this.initialNewBookingIds.length === 0) {
                // First time loading - store initial IDs
                this.initialNewBookingIds = [...currentIds];
                this.newBookingIds = [...currentIds];
            } else {
                // Use initial IDs to keep NEW tags visible until page refresh
                this.newBookingIds = [...this.initialNewBookingIds];
            }
            return Promise.resolve();
        },
        isNewBooking(guest) {
            // Use initialNewBookingIds to check (persists until page refresh)
            const idsToCheck = this.initialNewBookingIds.length > 0 ? this.initialNewBookingIds : this.newBookingIds;
            
            // Check if any bookingId in the guest's bookingId array matches any ID in newBookingIds
            if (!guest || !idsToCheck || idsToCheck.length === 0) {
                return false;
            }
            
            // bookingId can be an array or a string
            let bookingIds = [];
            if (guest.bookingId) {
                if (Array.isArray(guest.bookingId)) {
                    bookingIds = guest.bookingId;
                } else if (typeof guest.bookingId === 'string') {
                    // Try to parse as JSON array
                    try {
                        const parsed = JSON.parse(guest.bookingId);
                        if (Array.isArray(parsed)) {
                            bookingIds = parsed;
                        } else {
                            bookingIds = [guest.bookingId];
                        }
                    } catch (e) {
                        bookingIds = [guest.bookingId];
                    }
                }
            }
            
            // Check if any booking ID matches
            return bookingIds.some(bookingId => idsToCheck.includes(bookingId));
        },
        async markNotificationsAsRead() {
            // Prevent duplicate API calls
            if (this.hasMarkedAsRead || !this.objectId) {
                return;
            }
            
            // Only mark as read if there are new bookings
            const count = notificationService.getNotificationCount(this.objectId);
            if (count === 0) {
                return;
            }
            
            try {
                this.hasMarkedAsRead = true;
                await apiService.markBookingNotificationAsRead(this.objectId);
                // Refresh notifications from service after marking as read (updates badge counts)
                await notificationService.refreshNotifications();
                // Note: We don't update newBookingIds here - keep initialNewBookingIds
                // so NEW tags remain visible until page refresh
                // Emit event to parent components to refresh their notification counts
                this.$emit('notifications-read');
            } catch (error) {
                this.hasMarkedAsRead = false; // Reset flag on error so it can retry
                if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
                    return;
                }
                console.error('Error marking notifications as read:', error);
            }
        },

    },
};
</script>

<style scoped>
/* Main Container */
.guest-view-container {
    height: calc(100vh - 60px); /* Account for DashboardHeader */
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 0;
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Enhanced Loading Overlay */
.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e2e8f0;
    border-top: 4px solid #5b2e8e;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    box-shadow: 0 4px 12px rgba(91, 46, 142, 0.2);
}

.loading-text {
    color: #64748b;
    font-size: 16px;
    font-weight: 500;
    margin: 0;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Header Section */
.header-section {
    background: white;
    border-radius: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    margin-bottom: 0;
    border: none;
    border-bottom: 1px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 100;
    flex-shrink: 0;
    margin-top: 20px;
}

.header-content {
    padding: 16px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
}

.title-section {
    flex: 1;
}

.header-actions {
    display: flex;
    gap: 12px;
    align-items: center;
}

.icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
}

.page-title {
    text-align: left;
    display: flex;
    align-items: center;
    gap: 16px;
}

.title-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(91, 46, 142, 0.2);
}

.title-icon svg {
    width: 24px;
    height: 24px;
}

.title-content {
    flex: 1;
}

.page-title h1 {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 4px 0;
    line-height: 1.2;
    letter-spacing: -0.025em;
    text-align: left;
}

.subtitle {
    font-size: 14px;
    color: #64748b;
    margin: 0;
    line-height: 1.4;
    text-align: left;
}

.action-buttons {
    display: flex;
    gap: 12px;
    align-items: center;
}

/* Modern Button Styles */
.btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    text-decoration: none;
    position: relative;
    overflow: hidden;
}

.btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
}

.btn:hover::before {
    left: 100%;
}

.btn-primary {
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    box-shadow: 0 2px 6px rgba(91, 46, 142, 0.2);
    padding: 8px 16px;
    font-size: 14px;
}

.btn-primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
    background: linear-gradient(135deg, #4c1d75, #6b21d4);
}

.btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    border: 1px solid #e2e8f0;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
}

.btn-secondary:hover {
    background: #f8fafc;
    border-color: #5b2e8e;
    color: #5b2e8e;
}

/* Main Content */
.main-content {
    flex: 1;
    padding: 0;
    overflow: hidden;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

/* Stats Section */
.stats-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
    padding: 16px 24px;
    background: white;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
}

.stat-card {
    background: white;
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
    color: #64748b;
    flex-shrink: 0;
}

.stat-icon svg {
    width: 20px;
    height: 20px;
}

.stat-icon.active {
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    box-shadow: 0 2px 6px rgba(91, 46, 142, 0.2);
}

.stat-content {
    flex: 1;
    min-width: 0;
    text-align: left;
}

.stat-content h3 {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 2px 0;
    line-height: 1.2;
}

.stat-content p {
    font-size: 12px;
    color: #64748b;
    margin: 0;
    font-weight: 500;
}

/* Table Container */
.table-container {
    background: white;
    border-radius: 0;
    box-shadow: none;
    border: none;
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
}

/* Table Loading Overlay */
.table-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 100;
    backdrop-filter: blur(2px);
    border-radius: 16px;
}

.table-header {
    padding: 16px 24px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: white;
    flex-shrink: 0;
}

.table-header h2 {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.table-controls {
    display: flex;
    gap: 16px;
    align-items: center;
}

.search-box {
    position: relative;
    display: flex;
    align-items: center;
}

.search-box svg {
    position: absolute;
    left: 12px;
    color: #94a3b8;
    width: 16px;
    height: 16px;
    z-index: 1;
}

.search-box input {
    padding: 10px 14px 10px 40px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    width: 240px;
    transition: all 0.2s ease;
    background: #f8fafc;
}

.search-box input:focus {
    outline: none;
    border-color: #5b2e8e;
    box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

/* Modern Table */
.table-wrapper {
    overflow-x: auto;
    overflow-y: auto;
    min-height: 400px;
    flex: 1;
    min-height: 0;
}

.modern-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    table-layout: auto;
}

.modern-table.bookings-table {
    min-width: 800px;
    table-layout: fixed;
    width: 100%;
}

.modern-table th {
    background: #f8fafc;
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    color: #475569;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 10;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
}

.modern-table td {
    padding: 12px 16px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    vertical-align: middle;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 14px;
    text-align: left;
}

.modern-table th,
.modern-table td {
    vertical-align: middle;
}

/* Column alignment for bookings table - ensure headers and cells align */
.modern-table.bookings-table th.col-index,
.modern-table.bookings-table td.col-index {
    text-align: center;
    padding: 12px 8px;
}

.modern-table.bookings-table th.col-guest-id,
.modern-table.bookings-table td.col-guest-id {
    text-align: left;
    padding: 12px 16px;
}

.modern-table.bookings-table th.col-mobile,
.modern-table.bookings-table td.col-mobile {
    text-align: left;
    padding: 12px 16px;
}

.modern-table.bookings-table th.col-booking-id,
.modern-table.bookings-table td.col-booking-id {
    text-align: left;
    padding: 12px 16px;
}

.modern-table.bookings-table th.col-created,
.modern-table.bookings-table td.col-created {
    text-align: left;
    padding: 12px 16px;
}


.table-row {
    transition: all 0.2s ease;
}

.table-row:hover {
    background: linear-gradient(135deg, #faf7ff, #f3f0ff);
}

/* Column Specific Styles for Bookings Table */
.modern-table.bookings-table .col-index {
    width: 60px;
    min-width: 60px;
    max-width: 60px;
}

.modern-table.bookings-table .col-guest-id {
    width: 180px;
    min-width: 180px;
    max-width: 180px;
}

.modern-table.bookings-table .col-mobile {
    width: 200px;
    min-width: 200px;
    max-width: 200px;
}

.modern-table.bookings-table .col-booking-id {
    width: 200px;
    min-width: 200px;
    max-width: 200px;
}

.modern-table.bookings-table .col-created {
    width: 200px;
    min-width: 200px;
    max-width: 200px;
}


/* Booking Table Specific Styles */
.booking-row {
    transition: background-color 0.2s ease;
    position: relative;
}

.booking-row.new-booking-row {
    background: linear-gradient(90deg, rgba(59, 130, 246, 0.03) 0%, transparent 100%);
}

.booking-row:hover {
    background: linear-gradient(135deg, #faf7ff, #f3f0ff);
}

.booking-row.new-booking-row:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(59, 130, 246, 0.02));
}

.guest-id-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.guest-id-badge {
    display: inline-block;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    padding: 8px 16px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.5px;
    box-shadow: 0 2px 8px rgba(91, 46, 142, 0.2);
    transition: all 0.2s ease;
}

.booking-row:hover .guest-id-badge {
    box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}


.mobile-display {
    display: flex;
    align-items: center;
    gap: 10px;
}

.mobile-icon {
    width: 18px;
    height: 18px;
    color: #5b2e8e;
    flex-shrink: 0;
}

.mobile-number {
    font-weight: 600;
    color: #1e293b;
    font-size: 14px;
}

.booking-id-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.booking-id-badge {
    display: inline-block;
    background: linear-gradient(135deg, #f8fafc, #e2e8f0);
    color: #5b2e8e;
    padding: 8px 16px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.5px;
    border: 1px solid #e2e8f0;
    font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
    transition: all 0.2s ease;
}

.booking-row:hover .booking-id-badge {
    background: linear-gradient(135deg, #f3f0ff, #e9e5ff);
    border-color: #c4b5fd;
}

.new-booking-indicator {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    padding: 4px 10px;
    border-radius: 12px;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.25);
    line-height: 1;
    white-space: nowrap;
    transition: all 0.2s ease;
}

.booking-row:hover .new-booking-indicator {
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    box-shadow: 0 3px 6px rgba(59, 130, 246, 0.35);
    transform: translateY(-1px);
}

.date-display {
    display: flex;
    align-items: center;
    gap: 10px;
}

.date-icon {
    width: 18px;
    height: 18px;
    color: #5b2e8e;
    flex-shrink: 0;
}

.date-text {
    color: #64748b;
    font-size: 14px;
    font-weight: 500;
}

.guest-info {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0; /* Allow flex items to shrink */
}

.guest-avatar {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 12px;
    flex-shrink: 0; /* Prevent avatar from shrinking */
}

.guest-name-t {
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0; /* Allow text to shrink */
}


.room-number {
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    padding: 4px 12px;
    border-radius: 16px;
    font-weight: 600;
    font-size: 12px;
    white-space: nowrap;
    display: inline-block;
}

.status-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    text-transform: capitalize;
    white-space: nowrap;
    display: inline-block;
}

.status-active {
    background: #dcfce7;
    color: #166534;
}

.status-inactive {
    background: #fef2f2;
    color: #dc2626;
}

.status-pending {
    background: #fef3c7;
    color: #d97706;
}

.status-checked-out {
    background: #e0e7ff;
    color: #4338ca;
}

.status-default {
    background: #f1f5f9;
    color: #64748b;
}

.mode-badge {
    background: #f1f5f9;
    color: #475569;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    display: inline-block;
}

.date-text {
    color: #64748b;
    font-size: 14px;
    white-space: nowrap;
}

.no-data {
    color: #94a3b8;
    font-style: italic;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 64px 32px;
}

.empty-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    color: #64748b;
}

.empty-icon {
    width: 64px;
    height: 64px;
    color: #cbd5e1;
    margin-bottom: 8px;
}

.empty-content h3 {
    font-size: 18px;
    font-weight: 600;
    color: #475569;
    margin: 0;
}

.empty-content p {
    font-size: 14px;
    margin: 0;
    color: #94a3b8;
}

/* Enhanced Pagination */
.pagination-wrapper {
    padding: 16px 24px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: white;
    flex-shrink: 0;
}

.pagination-info {
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
}

.pagination {
    display: flex;
    align-items: center;
    gap: 8px;
}

.pagination-btn {
    min-width: 40px;
    height: 40px;
    border: 1px solid #e2e8f0;
    background: white;
    color: #475569;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled):not(.ellipsis) {
    border-color: #5b2e8e;
    color: #5b2e8e;
    background: #faf7ff;
    transform: translateY(-1px);
}

.pagination-btn.active {
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    border-color: #5b2e8e;
    box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}

.pagination-btn:disabled {
    background: #f8fafc;
    color: #cbd5e1;
    cursor: not-allowed;
    border-color: #f1f5f9;
}

.pagination-btn.ellipsis {
    border: none;
    background: transparent;
    cursor: default;
}

.prev-btn,
.next-btn {
    width: 40px;
    height: 40px;
}

.prev-btn svg,
.next-btn svg {
    width: 16px;
    height: 16px;
}

/* QR Column Styles */
.col-qr {
    width: 120px;
    text-align: center;
}

.qr-actions {
    display: flex;
    gap: 6px;
    justify-content: center;
    align-items: center;
}

.btn-qr-view,
.btn-qr-download {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 10px;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;
    white-space: nowrap;
}

.btn-qr-view {
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    box-shadow: 0 2px 8px rgba(91, 46, 142, 0.3);
}

.btn-qr-view:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(91, 46, 142, 0.4);
}

.btn-qr-download {
    background: #f8fafc;
    color: #64748b;
    border: 1px solid #e2e8f0;
}

.btn-qr-download:hover {
    background: #e2e8f0;
    color: #475569;
    transform: translateY(-1px);
}

.qr-inactive {
    color: #94a3b8;
    font-style: italic;
    font-size: 12px;
}

/* QR Modal Styles */
.qr-modal {
    background: white;
    border-radius: 16px;
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    animation: slideUp 0.3s ease-out;
    position: relative;
}

.qr-modal-header {
    background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
    color: white;
    padding: 24px 32px;
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.qr-modal-title {
    display: flex;
    align-items: center;
    gap: 16px;
}

.qr-icon {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.qr-modal-title h3 {
    margin: 0 0 4px 0;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
}

.qr-modal-title p {
    margin: 0;
    font-size: 14px;
    opacity: 0.9;
    font-weight: 400;
}

.qr-close-btn {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 8px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: white;
}

.qr-close-btn:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
}

.qr-modal-content {
    padding: 32px;
}

.qr-code-display {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    margin-bottom: 32px;
}

.qr-container {
    background: #ffffff;
    border: 2px solid #e5e7eb;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.qr-canvas {
    display: block;
    border-radius: 8px;
}

.qr-info {
    text-align: center;
    max-width: 300px;
}

.qr-description {
    margin: 0 0 16px 0;
    font-size: 16px;
    color: #64748b;
    line-height: 1.5;
}

.qr-details {
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 14px;
    color: #475569;
}

.qr-details span {
    padding: 8px 12px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.qr-modal-actions {
    display: flex;
    gap: 16px;
    justify-content: center;
}

.btn-qr-primary {
    background: linear-gradient(135deg, #5b2e8e 0%, #7c3aed 100%);
    color: white;
    border: none;
    border-radius: 12px;
    padding: 14px 28px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 48px;
}

.btn-qr-primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 25px -5px rgba(91, 46, 142, 0.4);
}

.btn-qr-secondary {
    background: #ffffff;
    color: #6b7280;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    padding: 14px 28px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 48px;
}

.btn-qr-secondary:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    transform: translateY(-1px);
}

/* Modal Overlay */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
}

/* Enhanced Guest Profile Modal Styles */
.profile-modal {
    background: white;
    border-radius: 20px;
    width: 95%;
    max-width: 900px;
    max-height: 95vh;
    overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05);
    animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    position: relative;
}

/* Enhanced Header */
.profile-modal-header {
    position: relative;
    overflow: hidden;
    color: white;
    padding: 0;
}

.header-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    opacity: 0.9;
}

.header-background::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.03' fill-rule='evenodd'%3E%3Cpath d='m0 40l40-40h-40v40zm40 0v-40h-40l40 40z'/%3E%3C/g%3E%3C/svg%3E") repeat;
}

.profile-header-content {
    position: relative;
    z-index: 2;
    padding: 32px 40px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
}

.profile-header-left {
    display: flex;
    gap: 24px;
    align-items: center;
}

.profile-main-image {
    position: relative;
    flex-shrink: 0;
}

.profile-img,
.profile-main-avatar {
    width: 100px;
    height: 100px;
    border-radius: 20px;
    border: 4px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.profile-img {
    object-fit: cover;
}

.profile-main-avatar {
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 32px;
    color: white;
}

.profile-status-indicator {
    position: absolute;
    bottom: 8px;
    right: 8px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 3px solid white;
    display: flex;
    align-items: center;
    justify-content: center;
}

.profile-status-indicator.status-active {
    background: #10b981;
}

.profile-status-indicator.status-inactive {
    background: #ef4444;
}

.profile-status-indicator.status-pending {
    background: #f59e0b;
}

.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: white;
}

.profile-header-info {
    flex: 1;
}

.guest-name {
    font-size: 28px;
    font-weight: 800;
    margin: 0 0 8px 0;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.guest-id-text {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.945);
    margin: 0 0 16px 0;
    font-weight: 500;
}

.guest-id-text-t {
    font-size: 16px;
    color: rgba(0, 0, 0, 0.8);
    margin: 0 0 16px 0;
    font-weight: 500;
}

.header-status-badges {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.status-badge-sm {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.status-badge-sm.status-active {
    background: rgba(16, 185, 129, 0.9);
    color: white;
}

.status-badge-sm.status-pending {
    background: rgba(245, 158, 11, 0.9);
    color: white;
}

.profile-close-btn {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    color: white;
}

.profile-close-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
}

/* Enhanced Content Area */
.profile-modal-content {
    max-height: calc(95vh - 180px);
    overflow-y: auto;
    padding: 0;
    background: #f8fafc;
}

.profile-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 32px;
    gap: 20px;
    background: white;
}

.profile-loading .spinner {
    width: 40px;
    height: 40px;
}

.profile-loading p {
    color: #64748b;
    font-size: 18px;
    font-weight: 500;
    margin: 0;
}

.profile-details {
    padding: 32px;
    background: #f8fafc;
}

/* Quick Stats Section */
.quick-stats-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 32px;
}

.stat-card-sm {
    background: white;
    border-radius: 16px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
    transition: all 0.3s ease;
}

.stat-card-sm:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px -5px rgba(0, 0, 0, 0.15);
}

.stat-icon-sm {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.stat-content-sm {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.stat-label {
    font-size: 12px;
    color: #64748b;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
}

.stat-value {
    font-size: 16px;
    color: #1e293b;
    font-weight: 700;
}

/* Enhanced Information Cards */
.profile-info-container {
    display: grid;
    gap: 24px;
}

.info-card {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
    transition: all 0.3s ease;
}

.info-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px -5px rgba(0, 0, 0, 0.15);
}

.card-header {
    background: linear-gradient(135deg, #f8fafc, #e2e8f0);
    padding: 20px 24px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    gap: 12px;
}

.card-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.card-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
}

.card-content {
    padding: 24px;
}

.info-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
    margin-bottom: 16px;
}

.info-row:last-child {
    margin-bottom: 0;
}

.info-field {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.info-field svg {
    color: #667eea;
    margin-top: 2px;
    flex-shrink: 0;
}

.field-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.field-content label {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.field-content span {
    font-size: 15px;
    color: #1e293b;
    font-weight: 500;
    word-break: break-word;
}

/* Address Display */
.address-display {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.address-line {
    font-size: 15px;
    color: #1e293b;
    line-height: 1.5;
}

.address-line strong {
    font-weight: 700;
    color: #0f172a;
}

/* Booking ID styling */
.booking-id {
    font-family: 'Monaco', 'Menlo', monospace;
    background: #f1f5f9;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 13px !important;
    border: 1px solid #e2e8f0;
}

/* ID Verification */
.id-verification-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.id-type-info {
    display: flex;
    justify-content: center;
}

.id-type-badge {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.id-images {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
}

.id-image-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.id-image-container label {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: center;
}

.id-image-enhanced {
    width: 100%;
    height: 140px;
    object-fit: cover;
    border-radius: 12px;
    border: 2px solid #e2e8f0;
    background: #f8fafc;
    transition: all 0.3s ease;
}

.id-image-enhanced:hover {
    border-color: #667eea;
    transform: scale(1.02);
}

/* System Information */
.system-info {
    background: linear-gradient(135deg, #f8fafc, #f1f5f9);
}

.system-info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 16px;
}

.system-info-item {
    text-align: center;
    padding: 16px;
    background: white;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.system-info-item label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
}

.system-info-item span {
    font-size: 14px;
    color: #1e293b;
    font-weight: 600;
}

/* Modal Actions */
.profile-modal-actions {
    padding: 24px 32px;
    background: white;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: center;
}

.btn-profile-close {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    border: none;
    border-radius: 12px;
    padding: 16px 32px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 52px;
    box-shadow: 0 4px 14px rgba(102, 126, 234, 0.3);
}

.btn-profile-close:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

/* Animation */
@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Responsive Design */
@media (max-width: 1024px) {
    .header-content {
        flex-direction: column;
        align-items: stretch;
        gap: 24px;
    }

    .header-actions {
        width: 100%;
        justify-content: stretch;
    }

    .btn-primary, .btn-secondary {
        flex: 1;
        justify-content: center;
    }

    .stats-section {
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 16px;
    }
}

@media (max-width: 768px) {
    .main-content {
        padding: 16px;
    }

    .header-content {
        padding: 24px;
    }

    .title-icon {
        width: 40px;
        height: 40px;
    }

    .title-icon svg {
        width: 20px;
        height: 20px;
    }

    .page-title h1 {
        font-size: 24px;
    }

    .header-actions {
        flex-direction: column;
        align-items: stretch;
        gap: 16px;
    }

    .action-buttons {
        flex-direction: column;
        align-items: stretch;
    }

    .btn {
        justify-content: center;
    }

    .stats-section {
        grid-template-columns: 1fr;
        gap: 12px;
        padding: 16px;
    }

    .stat-card {
        padding: 20px;
    }

    .table-header {
        padding: 20px;
        flex-direction: column;
        gap: 16px;
        align-items: stretch;
    }

    .table-controls {
        justify-content: stretch;
    }

    .search-box input {
        width: 100%;
    }

    .table-container {
        max-height: calc(100vh - 260px);
    }
    
    .modern-table {
        min-width: 1200px; /* Reduce minimum width for tablets */
    }
    
    .modern-table th,
    .modern-table td {
        padding: 12px 10px;
        font-size: 14px;
    }

    .guest-info {
        gap: 8px;
    }

    .guest-avatar {
        width: 32px;
        height: 32px;
        font-size: 12px;
    }

    .pagination-wrapper {
        padding: 20px;
        flex-direction: column;
        gap: 16px;
        align-items: stretch;
    }

    .pagination {
        justify-content: center;
        flex-wrap: wrap;
    }

    .pagination-btn {
        min-width: 36px;
        height: 36px;
        font-size: 14px;
    }

    .qr-actions {
        flex-direction: column;
        gap: 4px;
    }

    .btn-qr-view,
    .btn-qr-download {
        font-size: 12px;
        padding: 4px 8px;
    }

    .qr-modal {
        margin: 16px;
        width: calc(100% - 32px);
        max-height: calc(100vh - 32px);
    }

    .qr-modal-header {
        padding: 20px 24px;
    }

    .qr-modal-content {
        padding: 24px;
    }

    .qr-modal-actions {
        flex-direction: column;
    }

    .btn-qr-primary,
    .btn-qr-secondary {
        width: 100%;
    }

    .profile-modal {
        margin: 16px;
        width: calc(100% - 32px);
        max-height: calc(100vh - 32px);
        border-radius: 16px;
    }

    .profile-header-content {
        padding: 24px 28px;
        flex-direction: column;
        gap: 20px;
        text-align: center;
    }

    .profile-header-left {
        flex-direction: column;
        gap: 16px;
        align-items: center;
        width: 100%;
    }

    .profile-main-image {
        align-self: center;
    }

    .profile-img,
    .profile-main-avatar {
        width: 80px;
        height: 80px;
        border-radius: 16px;
    }

    .profile-main-avatar {
        font-size: 24px;
    }

    .guest-name {
        font-size: 24px;
    }

    .guest-id-text {
        font-size: 14px;
    }

    .header-status-badges {
        justify-content: center;
    }

    .profile-close-btn {
        position: absolute;
        top: 16px;
        right: 16px;
        width: 36px;
        height: 36px;
    }

    .profile-details {
        padding: 24px 20px;
    }

    .quick-stats-section {
        grid-template-columns: 1fr;
        gap: 16px;
        margin-bottom: 24px;
    }

    .stat-card-sm {
        padding: 16px;
        gap: 12px;
    }

    .stat-icon-sm {
        width: 40px;
        height: 40px;
    }

    .info-row {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    .card-header {
        padding: 16px 20px;
    }

    .card-content {
        padding: 20px;
    }

    .id-images {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    .system-info-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .profile-modal-actions {
        padding: 20px 24px;
    }

    .btn-profile-close {
        width: 100%;
        padding: 14px 28px;
        min-height: 48px;
    }
}

@media (max-width: 480px) {
    .main-content {
        padding: 12px;
    }

    .header-content {
        padding: 20px;
    }

    .title-icon {
        width: 36px;
        height: 36px;
    }

    .title-icon svg {
        width: 18px;
        height: 18px;
    }

    .page-title {
        gap: 12px;
    }

    .page-title h1 {
        font-size: 20px;
    }

    .subtitle {
        font-size: 14px;
    }

    .btn {
        padding: 10px 20px;
        font-size: 14px;
    }

    .stat-card {
        padding: 16px;
        gap: 16px;
    }

    .stat-icon {
        width: 48px;
        height: 48px;
        font-size: 20px;
    }

    .stat-content h3 {
        font-size: 24px;
    }

    .table-header {
        padding: 16px;
    }

    .table-header h2 {
        font-size: 18px;
    }

    .modern-table {
        min-width: 1000px; /* Further reduce for mobile */
        font-size: 12px;
    }
    
    .modern-table th,
    .modern-table td {
        padding: 10px 8px;
        font-size: 12px;
    }

    .pagination-wrapper {
        padding: 16px;
    }

    .pagination-btn {
        min-width: 32px;
        height: 32px;
        font-size: 12px;
    }

    .col-qr {
        width: 80px;
    }

    .qr-actions {
        flex-direction: column;
        gap: 2px;
    }

    .btn-qr-view,
    .btn-qr-download {
        font-size: 12px;
        padding: 3px 6px;
        gap: 2px;
    }

    .btn-qr-view svg,
    .btn-qr-download svg {
        width: 12px;
        height: 12px;
    }

    .qr-modal-header {
        padding: 16px 20px;
    }

    .qr-modal-content {
        padding: 20px;
    }

    .qr-container {
        padding: 16px;
    }

    .qr-canvas {
        width: 160px !important;
        height: 160px !important;
    }
}

/* Toast Notification Styles */
.toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 400px;
    pointer-events: none;
}

.toast {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    border-left: 4px solid transparent;
    min-width: 320px;
    pointer-events: auto;
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.toast-error {
    border-left-color: #ef4444;
    background: linear-gradient(135deg, #fef2f2, #ffffff);
}

.toast-success {
    border-left-color: #10b981;
    background: linear-gradient(135deg, #f0fdf4, #ffffff);
}

.toast-info {
    border-left-color: #3b82f6;
    background: linear-gradient(135deg, #eff6ff, #ffffff);
}

.toast-warning {
    border-left-color: #f59e0b;
    background: linear-gradient(135deg, #fffbeb, #ffffff);
}

.toast-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
}

.toast-error .toast-icon {
    color: #ef4444;
}

.toast-success .toast-icon {
    color: #10b981;
}

.toast-info .toast-icon {
    color: #3b82f6;
}

.toast-warning .toast-icon {
    color: #f59e0b;
}

.toast-content {
    flex: 1;
    min-width: 0;
}

.toast-message {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    line-height: 1.4;
    word-wrap: break-word;
}

.toast-close {
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.toast-close:hover {
    color: #6b7280;
    background: rgba(0, 0, 0, 0.05);
    transform: scale(1.1);
}

/* Toast Animations */
.toast-enter-active {
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.toast-enter-from {
    opacity: 0;
    transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
    opacity: 0;
    transform: translateX(100%) scale(0.95);
}

.toast-move {
    transition: transform 0.3s ease;
}

/* Responsive Toast Styles */
@media (max-width: 768px) {
    .toast-container {
        top: 16px;
        right: 16px;
        left: 16px;
        max-width: none;
    }

    .toast {
        min-width: auto;
        width: 100%;
        padding: 14px 16px;
    }

    .toast-message {
        font-size: 14px;
    }
}

/* Print Styles */
@media print {
    .guest-view-container {
        background: white;
        padding: 0;
    }

    .header-section,
    .action-buttons,
    .pagination-wrapper,
    .search-box,
    .qr-actions,
    .toast-container {
        display: none;
    }

    .table-container {
        box-shadow: none;
        border: 1px solid #ccc;
        max-height: calc(100vh - 280px);
    }

    .modern-table th,
    .modern-table td {
        padding: 8px;
        font-size: 12px;
    }
}
</style>